import { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import type { VesselPosition, BaseVesselData, StaticVesselData } from '../types/vessel';
import { getVesselName, hasValidCoordinates } from '../utils/vessel';

interface SearchOverlayProps {
  vessels: Record<string, VesselPosition>;
  baseData: Record<string, BaseVesselData>;
  staticData: Record<string, StaticVesselData>;
  onSearch: (query: string, matchingIds: string[]) => void;
  onFlyToVessel?: (id: string) => void;
  sidebarCollapsed: boolean;
}

export function SearchOverlay({
  vessels,
  baseData,
  staticData,
  onSearch,
  onFlyToVessel,
  sidebarCollapsed,
}: SearchOverlayProps) {
  const [query, setQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  const searchResults = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return Object.entries(vessels)
      .filter(([id, v]) => {
        if (!hasValidCoordinates(v)) {
          return false;
        }

        const name = getVesselName(v, id, baseData[id], staticData[id]).toLowerCase();
        const imo = v.IMO?.toString() || id;
        return name.includes(q) || imo.includes(q);
      })
      .slice(0, 8);
  }, [query, vessels, baseData, staticData]);

  const handleSearch = (value: string) => {
    setQuery(value);
    setShowDropdown(value.trim().length > 0);
    
    if (!value.trim()) {
      onSearch('', []);
      return;
    }

    const q = value.toLowerCase();
    const matches = Object.entries(vessels)
      .filter(([id, v]) => {
        if (!hasValidCoordinates(v)) {
          return false;
        }

        const name = getVesselName(v, id, baseData[id], staticData[id]).toLowerCase();
        const imo = v.IMO?.toString() || id;
        return name.includes(q) || imo.includes(q);
      })
      .map(([id]) => id);

    onSearch(value, matches);
  };

  const handleSelectVessel = (id: string) => {
    setQuery('');
    setShowDropdown(false);
    onSearch('', []);
    onFlyToVessel?.(id);
  };

  const leftOffset = sidebarCollapsed ? '50%' : 'calc(220px + (100% - 220px) / 2)';

  return (
    <div style={{ position: 'absolute', top: '16px', left: leftOffset, transform: 'translateX(-50%)', zIndex: 25, transition: 'left 0.3s ease' }}>
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          placeholder="Search vessel..."
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => query.trim() && setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
          style={{
            width: '260px',
            height: '40px',
            padding: '0 14px 0 38px',
            background: 'white',
            border: 'none',
            borderRadius: '20px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
            fontSize: '13px',
            outline: 'none',
          }}
        />
        <FontAwesomeIcon
          icon={faSearch}
          style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#999', fontSize: '13px' }}
        />
        
        {/* Dropdown Results */}
        {showDropdown && searchResults.length > 0 && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '8px',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            maxHeight: '300px',
            overflowY: 'auto',
          }}>
            {searchResults.map(([id, vessel]) => {
              const name = getVesselName(vessel, id, baseData[id], staticData[id]);
              return (
                <div
                  key={id}
                  onClick={() => handleSelectVessel(id)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {name}
                  </span>
                  <span style={{ color: '#888', fontSize: '11px', marginLeft: '8px' }}>
                    {id}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
